import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Result, Button } from 'antd';

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        // Update state so the next render will show the fallback UI
        return {
            hasError: true,
            error
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log error details
        console.error('🚨 ErrorBoundary - Caught an error:', error);
        console.error('🚨 ErrorBoundary - Error info:', errorInfo);
        console.error('🚨 ErrorBoundary - Component stack:', errorInfo.componentStack);

        // Update state with error info
        this.setState({
            error,
            errorInfo
        });

        // You can also log the error to an error reporting service here
        // logErrorToService(error, errorInfo);
    }

    resetError = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback component
            if (this.props.fallback) {
                const FallbackComponent = this.props.fallback;
                return <FallbackComponent error={this.state.error!} resetError={this.resetError} />;
            }

            // Default fallback UI
            return (
                <div style={{ padding: '20px' }}>
                    <Result
                        status="error"
                        title="Something went wrong"
                        subTitle="An unexpected error occurred while rendering this component."
                        extra={[
                            <Button type="primary" key="retry" onClick={this.resetError}>
                                Try Again
                            </Button>,
                            <Button key="home" onClick={() => window.location.href = '/'}>
                                Go Home
                            </Button>
                        ]}
                    >
                        {import.meta.env.DEV && (
                            <div style={{
                                marginTop: '20px',
                                padding: '16px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                textAlign: 'left'
                            }}>
                                <h4>Error Details (Development Mode):</h4>
                                <pre style={{
                                    fontSize: '12px',
                                    color: '#d32f2f',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    {this.state.error?.toString()}
                                </pre>
                                {this.state.errorInfo && (
                                    <details style={{ marginTop: '10px' }}>
                                        <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                            Component Stack
                                        </summary>
                                        <pre style={{
                                            fontSize: '11px',
                                            color: '#666',
                                            marginTop: '8px',
                                            whiteSpace: 'pre-wrap'
                                        }}>
                                            {this.state.errorInfo.componentStack}
                                        </pre>
                                    </details>
                                )}
                            </div>
                        )}
                    </Result>
                </div>
            );
        }

        return this.props.children;
    }
}

export { ErrorBoundary };
export default ErrorBoundary;